import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { WarningCircle, Clock } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const SESSION_KEY = "finance_alert_shown";

/**
 * Popup affiché une fois par session à l'étudiant, si son échéancier de
 * scolarité comporte une échéance en retard ou approchant — pour être sûr
 * qu'il ne le découvre pas seulement via la cloche de notifications.
 */
export default function FinanceAlertPopup() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [alertData, setAlertData] = useState(null);

  useEffect(() => {
    if (!user || user.role !== "etudiant") return;
    if (sessionStorage.getItem(SESSION_KEY)) return;

    api.get("/finance/my-status")
      .then((r) => {
        const { late, upcoming } = r.data;
        if ((late && late.length > 0) || (upcoming && upcoming.length > 0)) {
          setAlertData({ late: late || [], upcoming: upcoming || [] });
        }
        sessionStorage.setItem(SESSION_KEY, "1");
      })
      .catch(() => {
        sessionStorage.setItem(SESSION_KEY, "1"); // n'insiste pas si la requête échoue (ex: pas de module finance actif)
      });
  }, [user]);

  if (!alertData) return null;

  const goToFinance = () => { setAlertData(null); navigate("/app"); };

  return (
    <Dialog open onOpenChange={(o) => !o && setAlertData(null)}>
      <DialogContent data-testid="finance-alert-popup">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <WarningCircle size={22} weight="fill" className="text-accent" /> Votre scolarité
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {alertData.late.length > 0 && (
            <div className="rounded-lg border border-accent/30 bg-accent/5 p-3">
              <p className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-accent">
                <WarningCircle size={16} weight="fill" /> Échéance(s) en retard
              </p>
              {alertData.late.map((i) => (
                <p key={i.id} className="text-sm text-muted-foreground">
                  <strong className="text-foreground">{i.label}</strong> — {i.remaining} restant(s), échue le {i.due_date}
                </p>
              ))}
            </div>
          )}
          {alertData.upcoming.length > 0 && (
            <div className="rounded-lg border border-border bg-secondary p-3">
              <p className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold">
                <Clock size={16} /> Échéance(s) à venir
              </p>
              {alertData.upcoming.map((i) => (
                <p key={i.id} className="text-sm text-muted-foreground">
                  <strong className="text-foreground">{i.label}</strong> — {i.amount}, due le {i.due_date}
                </p>
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground">Merci de vous rapprocher de l'administration pour régulariser votre situation si besoin.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setAlertData(null)}>Fermer</Button>
          <Button onClick={goToFinance} className="bg-primary hover:bg-primary/90">Voir mon tableau de bord</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
